/* =========================================================
   ATS — Gift Cart Slider (separate module)
   - Listens: ats:cart:updated (dispatched from ATS-cart-drawer.js)
   - Updates progress UI
   - Auto add/remove gifts (cumulative tiers)
   ========================================================= */

(function () {
  class ATSGiftCartSlider {
    constructor() {
      this._syncing = false;

      document.addEventListener('ats:cart:updated', (e) => {
        const cart = e?.detail?.cart;
        if (cart) this.onCartUpdated(cart);
        console.log(e.detail.cart);
      });
    }

    /* ---------- DOM + config ---------- */

    getRoot() {
      // Gift slider is inside the cart drawer
      const drawer = document.getElementById('ATS-CartDrawer');
      return drawer?.querySelector('[data-ats-cart-gift]') || null;
    }

    getConfig(root) {
      const script = root?.querySelector('[data-ats-cart-gift-json]');
      let raw = [];
      try {
        raw = JSON.parse(script?.textContent || '[]');
      } catch (_) {}

      const moneyFormat = root?.dataset?.moneyFormat || 'Rs.{{amount}}';

      const tiers = (raw || [])
        .filter((t) => t && Number(t.amount) > 0 && t.variant_id)
        .map((t) => {
          const amountRaw = Number(t.amount);

          // Heuristic:
          // - If settings amount looks like 499 / 999 => treat as currency units => *100
          // - If it looks like 49900 / 99900 => treat as already cents/paise
          // const amountCents = amountRaw >= 20000 ? Math.round(amountRaw) : Math.round(amountRaw * 100);
          const amountCents = Math.round(amountRaw * 100);

          return {
            key: String(t.key || ''),
            label: String(t.label || ''),
            variantId: Number(t.variant_id),
            amountCents,
          };
        })
        .filter((t) => t.key && t.variantId && t.amountCents > 0)
        .sort((a, b) => a.amountCents - b.amountCents);

      return {
        tiers,
        moneyFormat,
        msgLocked: root?.dataset?.msgLocked || '',
        msgUnlocked: root?.dataset?.msgUnlocked || '',
        msgComplete: root?.dataset?.msgComplete || '',
      };
    }

    /* ---------- Gift detection ---------- */

    isGiftItem(item) {
      const p = item?.properties || {};
      const v = p._ats_gift;
      return v === '1' || v === 1 || v === true || String(v).toLowerCase() === 'true';
    }

    giftTierKey(item) {
      const p = item?.properties || {};
      return p._ats_gift_tier_key ? String(p._ats_gift_tier_key) : '';
    }

    paidSubtotalCents(cart) {
      const items = cart?.items || [];
      return items.reduce((sum, i) => {
        if (this.isGiftItem(i)) return sum;
        const lp = (typeof i.final_line_price === 'number' ? i.final_line_price : i.line_price) || 0;
        return sum + lp;
      }, 0);
    }

    /* ---------- Money + message helpers ---------- */

    formatMoney(fmt, cents) {
      const value = (cents / 100).toFixed(2);
      return String(fmt || 'Rs.{{amount}}')
        .replace(/{{\s*amount\s*}}/g, value)
        .replace(/{{\s*amount_no_decimals\s*}}/g, String(Math.round(cents / 100)))
        .replace(/{{\s*amount_with_comma_separator\s*}}/g, value.replace('.', ','))
        .replace(
          /{{\s*amount_no_decimals_with_comma_separator\s*}}/g,
          String(Math.round(cents / 100)).replace('.', ',')
        );
    }

    applyMsgTemplate(tpl, vars) {
      if (!tpl) return '';
      return String(tpl)
        .replace(/\{\{\s*amount\s*\}\}/g, vars.amount || '')
        .replace(/\{\{\s*label\s*\}\}/g, vars.label || '')
        .replace(/\{\{\s*next_label\s*\}\}/g, vars.label || '');
    }

    /* ---------- Cart mutations (gifts) ---------- */

    async addGiftVariant(variantId, tierKey) {
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({
          id: Number(variantId),
          quantity: 1,
          properties: {
            _ats_gift: '1',
            _ats_gift_tier_key: String(tierKey || ''),
          },
        }),
      });
    }

    async removeLineByKey(lineKey) {
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: 0 }),
      });
    }

    /* ---------- UI update (read-only) ---------- */

    updateUI(root, cfg, cart) {
      const paidCents = this.paidSubtotalCents(cart);
      const maxCents = cfg.tiers[cfg.tiers.length - 1]?.amountCents || 1;

      const fill = root.querySelector('[data-ats-cart-gift-fill]');
      const msgMain = root.querySelector('[data-ats-cart-gift-msg-main]');
      const msgSub = root.querySelector('[data-ats-cart-gift-msg-sub]');
      const markersEl = root.querySelector('[data-ats-cart-gift-markers]');
      const labelsEl = root.querySelector('[data-ats-cart-gift-labels]');

      const pct = Math.max(0, Math.min(100, (paidCents / maxCents) * 100));
      if (fill) fill.style.width = `${pct}%`;

      if (markersEl) markersEl.innerHTML = '';
      if (labelsEl) labelsEl.innerHTML = '';

      cfg.tiers.forEach((t) => {
        const leftPct = Math.max(0, Math.min(100, (t.amountCents / maxCents) * 100));
        const unlocked = paidCents >= t.amountCents;

        const m = document.createElement('span');
        m.className = 'tw-absolute tw-top-1/2 tw-pointer-events-none';
        m.style.left = `${leftPct}%`;
        m.style.transform = 'translate(-50%, -50%)';
        m.style.opacity = unlocked ? '1' : '.35';
        m.style.fontSize = '12px';
        m.textContent = '🎁';
        markersEl.appendChild(m);

        if (labelsEl) {
          const l = document.createElement('span');
          l.className = 'tw-absolute tw-top-0 tw-whitespace-nowrap';
          l.style.left = `calc(${leftPct}% - 2%)`;
          l.style.transform = 'translateX(-50%)';
          l.style.opacity = unlocked ? '1' : '.75';
          l.textContent = t.label || '';
          labelsEl.appendChild(l);
        }
      });

      const achievedCount = cfg.tiers.filter((t) => paidCents >= t.amountCents).length;
      const next = cfg.tiers.find((t) => paidCents < t.amountCents);

      if (!next) {
        const main = this.applyMsgTemplate(cfg.msgComplete, { amount: '', label: '' }) || 'All gifts unlocked!';
        if (msgMain) msgMain.textContent = main;
        if (msgSub) msgSub.textContent = '';
        return;
      }

      const remaining = Math.max(0, next.amountCents - paidCents);
      const amountStr = this.formatMoney(cfg.moneyFormat, remaining);

      const tpl = achievedCount > 0 ? cfg.msgUnlocked : cfg.msgLocked;
      const main =
        this.applyMsgTemplate(tpl, { amount: amountStr, label: next.label }) ||
        `Add ${amountStr} more to unlock ${next.label || 'your next gift'}.`;

      if (msgMain) msgMain.textContent = main;
      // Sub line: always show "Add {{amount}}..." style message for next tier
      const subTpl = cfg.msgLocked || 'Add {{amount}} more to unlock your free gift 🎁';
      const subText =
        this.applyMsgTemplate(subTpl, { amount: amountStr, label: next.label }) ||
        `Add ${amountStr} more to unlock your free gift 🎁`;

      if (msgSub) {
        if (achievedCount > 0) {
          msgSub.textContent = subText;
        } else {
          msgSub.textContent = '';
        }
      }
    }

    /* ---------- Gift sync (cumulative tiers) ---------- */

    async syncGifts(cfg, cart) {
      if (this._syncing) return false;

      const paidCents = this.paidSubtotalCents(cart);

      const achieved = cfg.tiers.filter((t) => paidCents >= t.amountCents);
      const desiredTierKeys = new Set(achieved.map((t) => t.key));
      const desiredVariantIds = new Set(achieved.map((t) => t.variantId));

      const items = cart?.items || [];
      const giftItems = items.filter((i) => this.isGiftItem(i));

      let changed = false;

      // Remove gifts no longer eligible
      for (const gi of giftItems) {
        const tierKey = this.giftTierKey(gi);
        const keepByKey = tierKey && desiredTierKeys.has(tierKey);
        const keepByVid = desiredVariantIds.has(Number(gi.variant_id));
        if (!keepByKey && !keepByVid) {
          await this.removeLineByKey(gi.key);
          changed = true;
        }
      }

      // Add missing gifts
      const existingGiftTierKeys = new Set(giftItems.map((i) => this.giftTierKey(i)).filter(Boolean));
      const existingGiftVariantIds = new Set(giftItems.map((i) => Number(i.variant_id)));

      for (const t of achieved) {
        const hasTierGift = existingGiftTierKeys.has(t.key) || existingGiftVariantIds.has(t.variantId);
        if (!hasTierGift) {
          await this.addGiftVariant(t.variantId, t.key);
          changed = true;
        }
      }

      return changed;
    }

    /* ---------- event handler ---------- */

    async onCartUpdated(cart) {
      const root = this.getRoot();
      if (!root) return;

      const cfg = this.getConfig(root);
      if (!cfg.tiers.length) return;

      // Always update UI (even while syncing)
      this.updateUI(root, cfg, cart);

      // If we are already in a gift-triggered refresh cycle, don't resync
      if (this._syncing) return;

      // Try to sync gifts; if changed, refresh drawer once
      try {
        const changed = await this.syncGifts(cfg, cart);
        if (changed && window.ATS?.cart?.refresh) {
          this._syncing = true;
          await window.ATS.cart.refresh(); // will dispatch ats:cart:updated again
        }
      } catch (e) {
        console.error('Gift slider error:', e);
      } finally {
        this._syncing = false;
      }
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    window.ATS = window.ATS || {};
    if (!window.ATS.giftSlider) window.ATS.giftSlider = new ATSGiftCartSlider();
  });
})();
